import {
  DeleteRequest,
  PutRequest,
  useLazySamplesSurfaceMicrobialSampleSetListQuery,
  useSamplesSurfaceMicrobialSampleSetDeleteMutation,
  useSamplesSurfaceMicrobialSampleSetListQuery,
  useSamplesSurfaceMicrobialSampleSetPutMutation,
  useSamplesSurfaceMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/surface-microbial-api";
import { SurfaceMicrobialSampleSet } from "@/services/backend/samples/types/surface-microbial-sample";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  documentDbGetAll,
  documentDbGetAllDeleted,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Mutation,
  useDocumentDBDelete,
  useDocumentDBGet,
  useDocumentDBGetAll,
  useDocumentDBPut,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useParams } from "react-router";
import { FilterMode } from "@/shared/lib/filter/filter";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

/*
 * useSurfaceMicrobialSampleSetOffline prepares two functions. One to
 * prepare the surface microbial sample sets for offline usage and one to
 * sync the offline state back to the server.
 *
 * When calling the functions, the offline database must
 * be set up. The caller must catch thrown errors.
 */
export function useSurfaceMicrobialSampleSetOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazySamplesSurfaceMicrobialSampleSetListQuery();
  const [put] = useSamplesSurfaceMicrobialSampleSetPutMutation();
  const [doDelete] = useSamplesSurfaceMicrobialSampleSetDeleteMutation();

  const prepMsg = t("Speichere Oberflächenkeimproben ...");
  const syncMsg = t("Synchronisiere Oberflächenkeimproben ...");
  const doneMsg = t("Oberflächenkeimproben");

  const prepareSurfaceMicrobialSampleSets = async () => {
    // Load the latest state
    const result = await load({
      document: { active: true, values: [workOrderId] },
    });
    if (result.error) {
      throw result.error;
    }

    // When going offline, data is set to stale, such that
    // all data is reloaded from the offline database.
    addStale("surfaceMicrobialSampleSet");

    // Append to synced state for user information
    setSynced((prev) => [...prev, prepMsg]);

    // Save to index DB
    await Promise.all(
      result.data!.sampleSets.map(async (sampleSet) => {
        await documentDbPut<SurfaceMicrobialSampleSet>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "surfaceMicrobialSampleSet",
          sampleSet,
        );
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  const syncSurfaceMicrobialSampleSets = async () => {
    // Append to synced state for user information
    setSynced((prev) => [...prev, syncMsg]);

    // Load from index DB
    const idbSampleSets = await documentDbGetAll<SurfaceMicrobialSampleSet>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "surfaceMicrobialSampleSet",
    );
    const deletedIdbSampleSets =
      await documentDbGetAllDeleted<SurfaceMicrobialSampleSet>(
        GSMDocumentKind.WorkOrder,
        workOrderId,
        "surfaceMicrobialSampleSet",
      );

    // Sync to server
    // We first PUT all non-deleted or new entities back to the server
    await Promise.all(
      idbSampleSets.map(async (sampleSet) => {
        await put(sampleSet).unwrap();
      }),
    );
    // Then we delete the entities that where deleted offline.
    await Promise.all(
      deletedIdbSampleSets.map(async (sampleSet) => {
        await doDelete({ id: sampleSet.id }).unwrap();
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === syncMsg ? doneMsg : p)));
  };

  return {
    prepareSurfaceMicrobialSampleSets,
    syncSurfaceMicrobialSampleSets,
  };
}

export function usePutSurfaceMicrobialSampleSet(): Mutation<
  SurfaceMicrobialSampleSet,
  SurfaceMicrobialSampleSet
> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [
    putSurfaceMicrobialSampleSetMutation,
    { isLoading, error, isSuccess, reset },
  ] = useSamplesSurfaceMicrobialSampleSetPutMutation();

  const [
    putSurfaceMicrobialSampleSetIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBPut<PutRequest, SurfaceMicrobialSampleSet>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "surfaceMicrobialSampleSet",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [
        putSurfaceMicrobialSampleSetMutation,
        { isLoading, error, isSuccess, reset },
      ];
    case OfflineState.OFFLINE:
      return [
        putSurfaceMicrobialSampleSetIndexedDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Put of sample set not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}

export function useGetSurfaceMicrobialSampleSet(id: string) {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesSurfaceMicrobialSampleSetShowQuery({
    id,
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGet<SurfaceMicrobialSampleSet>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "surfaceMicrobialSampleSet",
    id,
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useListSurfaceMicrobialSampleSets() {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesSurfaceMicrobialSampleSetListQuery({
    document: {
      active: true,
      mode: FilterMode.Or,
      values: [workOrderId!],
    },
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGetAll<SurfaceMicrobialSampleSet>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "surfaceMicrobialSampleSet",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.sampleSets,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useDeleteSurfaceMicrobialSampleSet(): Mutation<
  DeleteRequest,
  null
> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [
    deleteSurfaceMicrobialSampleSetMutation,
    { isLoading, error, isSuccess, reset },
  ] = useSamplesSurfaceMicrobialSampleSetDeleteMutation();

  const [
    deleteSurfaceMicrobialSampleSetIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBDelete(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "surfaceMicrobialSampleSet",
  );
  const deleteIdb = (request: DeleteRequest) =>
    deleteSurfaceMicrobialSampleSetIndexedDB(request.id);

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [
        deleteSurfaceMicrobialSampleSetMutation,
        { isLoading, error, isSuccess, reset },
      ];
    case OfflineState.OFFLINE:
      return [
        deleteIdb,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Delete of sample set not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}
